import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import {
  Lightning,
  Microphone,
  FileText,
  ArrowRight,
  Sparkle,
  Clock,
  CheckCircle,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * SmartToolsUpgradeCTA - Standardized upgrade prompt for Smart Tools
 * 
 * Used at lock points:
 * - Voice Notes (when trial exhausted)
 * - Job Summary Regenerate (when trial exhausted)
 * 
 * Features:
 * - Shows trial remaining if > 0
 * - Shows full upgrade CTA when trial exhausted
 * - Routes to existing /settings/upgrades?feature=smart_tools
 * - Tracks CTA views/clicks for analytics
 */
export default function SmartToolsUpgradeCTA({
  location = "unknown",     // "voice_notes" | "job_summary" - for tracking
  trialRemaining = 0,       // Remaining trial uses
  trialLimit = 0,           // Total trial limit
  compact = false,          // Compact mode for inline display
  showTrialInfo = true,     // Whether to show trial badge when remaining > 0
  onUpgradeClick,           // Optional callback before navigation
}) {
  const navigate = useNavigate();
  const currentLocation = useLocation();
  const [tracked, setTracked] = useState(false);

  // Track CTA view (once per mount)
  const trackView = async () => {
    if (tracked) return;
    setTracked(true);
    try {
      await fetch(`${API}/analytics/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          event: "smart_tools_upgrade_cta_viewed",
          properties: { location, trial_remaining: trialRemaining }
        })
      });
    } catch (e) {
      // Non-critical, ignore errors
    }
  };

  // Track on first render
  if (!tracked) {
    trackView();
  }

  const handleUpgradeClick = async () => {
    // Track click
    try {
      await fetch(`${API}/analytics/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          event: "smart_tools_upgrade_cta_clicked",
          properties: { location, trial_remaining: trialRemaining }
        })
      });
    } catch (e) {
      // Non-critical
    }

    // Optional callback
    if (onUpgradeClick) {
      onUpgradeClick();
    }

    // Navigate to upgrades page with Smart Tools preselected
    // Store return path in sessionStorage for post-purchase return
    sessionStorage.setItem("smart_tools_upgrade_return_path", currentLocation.pathname);
    navigate("/settings/upgrades?feature=smart_tools");
  };

  // If trial remaining and compact mode, just show a small badge with upgrade link
  if (showTrialInfo && trialRemaining > 0 && compact) {
    return (
      <div className="flex items-center gap-2">
        <Badge className="bg-blue-50 text-blue-700 text-xs">
          <Clock size={12} className="mr-1" />
          {trialRemaining} of {trialLimit} free
        </Badge>
        <button
          onClick={handleUpgradeClick}
          className="text-xs text-[#FF6600] hover:underline"
          data-testid="upgrade-smart-tools-link"
        >
          Upgrade anytime
        </button>
      </div>
    );
  }

  // Full upgrade CTA (when trial exhausted or full display requested)
  const benefits = [
    { icon: Microphone, text: "Unlimited voice notes" },
    { icon: FileText, text: "AI job summaries" },
    { icon: Sparkle, text: "Save 2+ hours per day" },
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg">
        <Lightning size={24} className="text-[#FF6600] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-zinc-800">
            {trialRemaining > 0 
              ? `${trialRemaining} free uses remaining`
              : "Upgrade to keep using Smart Tools"
            }
          </p>
        </div>
        <Button
          size="sm"
          onClick={handleUpgradeClick}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-1 flex-shrink-0"
          data-testid="upgrade-smart-tools-btn-compact"
        >
          Upgrade
          <ArrowRight size={14} />
        </Button>
      </div>
    );
  }

  return (
    <Card className="border-2 border-[#FF6600]/30 bg-gradient-to-br from-orange-50 via-white to-amber-50 overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className="p-3 bg-[#FF6600]/10 rounded-lg">
            <Lightning size={32} className="text-[#FF6600]" />
          </div>

          {/* Content */}
          <div className="flex-1 space-y-4">
            <div>
              <h3 className="font-heading text-lg font-bold text-zinc-900 uppercase">
                Unlock Smart Tools
              </h3>
              <p className="text-sm text-zinc-600 mt-1">
                {trialRemaining > 0 
                  ? `You have ${trialRemaining} free ${location === "voice_notes" ? "voice notes" : "AI regenerations"} remaining.`
                  : "Your free trial has ended. Upgrade to continue using AI features."
                }
              </p>
            </div>

            {/* Benefits */}
            <div className="flex flex-wrap gap-3">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm text-zinc-700">
                  <CheckCircle size={16} className="text-emerald-500" />
                  <span>{benefit.text}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={handleUpgradeClick}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white gap-2"
                data-testid="upgrade-smart-tools-btn"
              >
                <Lightning size={18} weight="fill" />
                Upgrade to Smart Tools
                <ArrowRight size={16} />
              </Button>
              {trialRemaining > 0 && (
                <span className="text-xs text-zinc-500">
                  Or use your {trialRemaining} remaining free {trialRemaining === 1 ? "use" : "uses"}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
